import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL = `${SITE.url}/write-for-us`;
const LAST_UPDATED = "July 2026";
const EDITOR_EMAIL = "editor@mapobserver.com";
const MAILTO =
  `mailto:${EDITOR_EMAIL}` +
  `?subject=${encodeURIComponent("Pitch: <headline in one line>")}` +
  `&body=${encodeURIComponent(
    [
      "The story in 2–3 sentences:",
      "",
      "Why it matters now:",
      "",
      "Sources / data / people you'll talk to:",
      "",
      "Expected length (600–2,500 words):",
      "",
      "About you (background + 2–3 clips or links):",
      "",
      "Any conflicts of interest to disclose:",
      "",
    ].join("\n"),
  )}`;

export const Route = createFileRoute("/write-for-us")({
  head: () => ({
    meta: [
      { title: `Write for ${SITE.name} — Pitch a Story` },
      {
        name: "description",
        content:
          "Independent reporting on local SEO, Google Business Profile, and the maps ecosystem. Pitch a story to the Map Observer editor.",
      },
      { property: "og:title", content: `Write for ${SITE.name}` },
      {
        property: "og:description",
        content:
          "How to pitch a reported story to Map Observer — beat, standards, format, rights, and response time.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12 prose-content">
        <p className="eyebrow">Contributors</p>
        <h1 className="headline-display text-4xl sm:text-5xl mt-3">
          Write for {SITE.name}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-foreground/90">
          {SITE.name} is an independent newsroom covering local search, Google
          Business Profile, and the maps ecosystem. We commission reported
          pieces from working journalists and practitioners who have something
          new to say — a story, data, or evidence the industry hasn't seen.
        </p>
        <p className="mt-6">
          <a
            href={MAILTO}
            className="inline-flex items-center gap-2 rounded-sm bg-accent px-5 py-3 text-sm font-medium text-accent-foreground hover:opacity-90 transition-opacity"
          >
            Pitch the editor →
          </a>
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Or copy the address: <span className="font-mono">{EDITOR_EMAIL}</span>
        </p>

        <hr className="my-12 border-border" />

        <section className="space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">What we publish</h2>
          <p>
            Our beat is the surface where Google, local businesses, and the
            people optimising for them collide. In practice that means:
            algorithm updates and volatility, GBP policy and product changes,
            spam and enforcement, Map Pack behaviour, tools and platform
            shifts, investigations into bad actors, and rigorous
            practitioner deep-dives.
          </p>
          <p>
            <strong>Standards.</strong> We publish original reporting or
            original analysis. Named sources are strongly preferred; anonymous
            sources require editor approval and a documented reason. Every
            claim should be backed by evidence you can share — screenshots,
            data, documents, or on-record quotes. We don't republish or
            lightly rewrite other outlets' stories, and we don't run pieces
            that exist to promote a tool, agency, or client.
          </p>
          <p>
            <strong>What we don't publish.</strong> Link-bait listicles,
            AI-generated filler, undisclosed sponsored content, or generic
            "top 10 SEO tips" posts.
          </p>
        </section>

        <hr className="my-12 border-border" />

        <section className="space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">How to pitch</h2>
          <p>
            Email <span className="font-mono">{EDITOR_EMAIL}</span> with the
            subject line <span className="font-mono">Pitch: &lt;headline&gt;</span>.
            Keep the pitch tight — one screen is plenty. Include:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>The story in two or three sentences.</li>
            <li>Why it matters now — the news peg, or the reason readers need it this week.</li>
            <li>The sources, data, or people you'll draw on.</li>
            <li>Expected length. Most pieces run 600–2,500 words.</li>
            <li>A short note on your background, with two or three clips or links.</li>
          </ul>
          <p>
            <strong>Pitch first, don't send finished drafts on spec.</strong>{" "}
            Unsolicited full drafts are rarely commissioned; a good pitch is
            faster for both of us.
          </p>
          <p>
            <strong>Exclusivity.</strong> Pitches should be exclusive to{" "}
            {SITE.name} while under consideration. If you've offered the same
            story elsewhere, say so up front.
          </p>
          <p>
            <strong>Conflicts of interest.</strong> Disclose anything relevant
            — your employer, clients, tools or companies you're writing about,
            and any financial stake in the outcome.
          </p>
        </section>

        <hr className="my-12 border-border" />

        <section className="space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">Rights, bylines &amp; response time</h2>
          <p>
            <strong>Rights.</strong> Contributors retain copyright.{" "}
            {SITE.name} takes first publication rights and a perpetual,
            non-exclusive licence to host, syndicate, and include the piece in
            our archives, feeds, and newsletters.
          </p>
          <p>
            <strong>Bylines.</strong> Every published piece carries a byline
            and a short bio with one link of your choice.
          </p>
          <p>
            <strong>Editing.</strong> Editors may copyedit for clarity, house
            style, and legal risk. Material changes are reviewed with you
            before publication. Our{" "}
            <Link to="/editorial-standards" className="text-accent underline">
              editorial standards
            </Link>{" "}
            and{" "}
            <Link to="/corrections" className="text-accent underline">
              corrections policy
            </Link>{" "}
            apply to contributed work.
          </p>
          <p>
            <strong>Compensation.</strong> Contributions are unpaid at this
            stage. We're upfront about that so you can decide whether the
            byline and audience are worth your time.
          </p>
          <p>
            <strong>Response time.</strong> The editor responds to pitches
            within five business days. If you haven't heard back after ten
            business days, treat it as a pass — you're free to place the
            pitch elsewhere.
          </p>
        </section>

        <hr className="my-12 border-border" />

        <section className="space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">Ready to pitch?</h2>
          <p>
            Send it to <span className="font-mono">{EDITOR_EMAIL}</span>. If
            the story stands up, we'll commission it.
          </p>
          <p>
            <a
              href={MAILTO}
              className="inline-flex items-center gap-2 rounded-sm bg-accent px-5 py-3 text-sm font-medium text-accent-foreground hover:opacity-90 transition-opacity"
            >
              Pitch the editor →
            </a>
          </p>
        </section>

        <p className="mt-16 text-xs text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
